import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { authSelector, selectedOrganizationSelector } from "../../../../reducers/index.js";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import {
  organizationPropertyGet,
  organizationPropertySet,
  organizationPropertyDelete,
  organizationTestToken,
} from "../../../../api/index.js";
import { getIntegrations } from "../../../../actions/organization/activeintegrations.js";
import { Form, Modal, Image, Spinner } from "react-bootstrap";
import { Confirmation } from "../../../../common/confirmation.jsx";
import { Button } from "../../../../common/Button.tsx";

export function SettingsVelhoIntegration({ setShow, active, integrationKey, title, imgSrc, testIntegration }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const auth = useSelector(authSelector);
  const selectedOrganization = useSelector(selectedOrganizationSelector);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const formValues = watch();
  const [testResult, setTestResult] = useState(<br />);
  const orgId = selectedOrganization.businessIdentifier ?? auth.companyBusinessId;

  useEffect(() => {
    fetchCredentials();
  }, []);

  async function fetchCredentials() {
    const res = await organizationPropertyGet(orgId, integrationKey);

    if (res?.status === 200 && res.data) {
      Object.keys(res.data).forEach((k) => setValue(k, res.data[k]));
    }
  }

  async function saveCredentials(data) {
    const payload = {
      username: data.username,
      password: data.password,
    };

    const res = await organizationPropertySet(orgId, integrationKey, JSON.stringify(payload));

    if (res?.status === 200) {
      dispatch(getIntegrations(orgId));
      closeModal();
    }
  }

  async function deleteCredentials() {
    setTestResult(<br />);
    const res = await organizationPropertyDelete(orgId, integrationKey);

    if (res?.status === 200) {
      setValue("username", "");
      setValue("password", "");
      dispatch(getIntegrations(orgId));
    }
  }

  async function testToken() {
    setTestResult(<Spinner size="sm" variant="primary" />);
    try {
      const res = await organizationTestToken(testIntegration);
      if (res && res.status === 200) {
        switch (res.data) {
          case true:
            setTestResult(`${t("TokenValid", "Token is valid")}`);
            break;
          case false:
            setTestResult(`${t("TokenInvalid", "Token is invalid")}`);
            break;
          default:
            setTestResult(`${t("ApiError", "API error")}`);
        }
      } else {
        setTestResult(`${t("ApiError", "API error")}`);
      }
    } catch {
      setTestResult(`${t("ApiError", "API error")}`);
    }
  }

  const closeModal = () => setShow();

  const isInvalid = !formValues?.username?.trim() || !formValues?.password?.trim();

  return (
    <Modal show onHide={closeModal} size="lg">
      <Form onSubmit={handleSubmit(saveCredentials)}>
        <Modal.Header closeButton>
          <Modal.Title style={{ lineHeight: 2 }}>
            {imgSrc && <Image src={imgSrc} height={25} alt={title} />} {t("integration", "integraatio")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t("Username", "Käyttäjänimi")}</Form.Label>
            <Form.Control {...register("username", { required: true })} type="text" isInvalid={errors.username} />
            <Form.Control.Feedback type="invalid">
              {t("UsernameIsRequired", "Käyttäjä on pakollinen")}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("Password", "Salasana")}</Form.Label>
            <Form.Control {...register("password", { required: true })} type="password" isInvalid={errors.password} />
            <Form.Control.Feedback type="invalid">
              {t("PasswordIsRequired", "Salasana on pakollinen")}
            </Form.Control.Feedback>
          </Form.Group>

          {testResult}
        </Modal.Body>

        <Modal.Footer>
          {active && testIntegration && (
            <Button onClick={testToken}>{t("TestConnection", "Testaa yhteys")}</Button>
          )}
          {active && (
            <Confirmation title={`Poistetaanko ${title}-integraatio?`} actionFunction={deleteCredentials}>
              <Button variant="warning">{t("Deactivate", "Poista käytöstä")}</Button>
            </Confirmation>
          )}
          {!active && (
            <Confirmation title={`Tallennetaanko ${title}-integraatio?`} actionFunction={handleSubmit(saveCredentials)}>
              <Button variant="success" disabled={isInvalid}>
                {t("Activate", "Ota käyttöön")}
              </Button>
            </Confirmation>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
